import { useState } from 'react'
import { StepNavigation, type Step } from '@/shared/components/ui/step-navigation'
import { cn } from '@/shared/utils/cn'
import { useWizardState } from './useWizardState'
import { WizardActions } from './WizardActions'
import type { WizardProps, WizardStepNav } from './types'

/**
 * Orquestador genérico de pasos. Agnóstico al dominio: no conoce zod,
 * react-hook-form ni Apollo. Las features proveen los componentes de cada
 * paso y su validador.
 *
 * No abre ni cierra el contenedor (Dialog/Drawer/Page) — quien monta el
 * Wizard maneja ese estado externamente.
 */
export function Wizard({
  steps,
  onComplete,
  onCancel,
  submitLabel,
  loading,
  allowJumpToCompleted = true,
  className,
}: WizardProps) {
  const { currentIndex, isFirst, isLast, goNext, goPrev, goTo } = useWizardState({
    totalSteps: steps.length,
  })
  const [advancing, setAdvancing] = useState(false)

  const navSteps: Step[] = steps.map((step, idx) => ({
    id: step.id,
    label: step.label,
    status:
      idx < currentIndex ? 'completed' : idx === currentIndex ? 'current' : 'pending',
  }))

  const handleNext = async () => {
    const step = steps[currentIndex]
    if (!step) return
    setAdvancing(true)
    try {
      const ok = step.validate ? await step.validate() : true
      if (!ok) return
      if (isLast) {
        await onComplete()
      } else {
        goNext()
      }
    } finally {
      setAdvancing(false)
    }
  }

  const handleStepClick = (id: Step['id']) => {
    const targetIdx = steps.findIndex((s) => s.id === id)
    if (targetIdx === -1) return
    goTo(targetIdx)
  }

  const activeStep = steps[currentIndex]
  const busy = loading || advancing
  const disableNext = activeStep?.canAdvance === false

  const stepNav: WizardStepNav = {
    goTo: (stepId) => {
      const idx = steps.findIndex((s) => s.id === stepId)
      if (idx !== -1) goTo(idx)
    },
  }

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      <StepNavigation
        steps={navSteps}
        onStepClick={allowJumpToCompleted ? handleStepClick : undefined}
      />

      <div className="min-h-[200px]">{activeStep?.render(stepNav)}</div>

      <WizardActions
        isFirst={isFirst}
        isLast={isLast}
        loading={busy}
        disableNext={disableNext}
        submitLabel={submitLabel}
        onPrev={goPrev}
        onNext={handleNext}
        onCancel={onCancel}
      />
    </div>
  )
}
